/**
 * Simple Asset Service - SIMPLIFIED VERSION
 * Replaces the 1398-line complex version with clean API calls
 */

import { apiService } from './ApiService';

export interface Asset {
  id: string;
  name: string;
  description: string;
  files: AssetFiles;
  createdDate: string;
  updatedDate?: string;
  custom_data?: any;
  permission_level?: 'read' | 'write';
  /** May this person upload files to the site? Write access minus assignments with "Upload files" switched off. */
  can_upload_files?: boolean;
  poiCount?: number;
  locationCount?: number;
}

export interface AssetFiles {
  ifc?: AssetFile[];  // IFC files are now an array
  rvt?: AssetFile;    // Primary/default local RVT for backward compatibility
  rvtFiles?: AssetFile[]; // All local RVT uploads
  e57?: AssetFile;
  e57Sources?: AssetFile[];
  poi?: AssetFile;
  geotiff?: AssetFile;
  dwg?: AssetFile;
  dxf?: AssetFile;
  [key: string]: AssetFile | AssetFile[] | undefined;
}

export interface AssetFile {
  id: string;
  fileName: string;
  filePath: string;
  fileSize: number;
  uploadedAt: string;
  metadata?: Record<string, any>;
  panoramaCount?: number;
  /** Photos of this E57 file still waiting for their viewer copy (audit bug 76). */
  preparingCount?: number;
  totalPanoramaCount?: number;
  sourceFileId?: number | string;
  poiCount?: number;
  isDefault?: boolean; // For IFC files - only one can be default per asset
}

export interface CreateAssetRequest {
  name: string;
  description?: string;
}

export class AssetService {
  private selectedAssetId: string | null = null;

  constructor() {
    this.loadSelectedAssetFromStorage();
  }

  // Asset CRUD Operations - Simple API calls only
  async getAssets(): Promise<Asset[]> {
    try {
      const assets = await apiService.getAssets();
      return assets;
    } catch (error) {
      console.error('❌ Failed to get assets:', error);
      return [];
    }
  }

  async getAsset(id: string): Promise<Asset | null> {
    try {
      return await apiService.getAsset(id);
    } catch (error) {
      console.error(`❌ Failed to get asset ${id}:`, error);
      return null;
    }
  }

  async createAsset(data: CreateAssetRequest): Promise<Asset> {
    try {
      const asset = await apiService.createAsset(data);
      return asset;
    } catch (error) {
      console.error('❌ Failed to create asset:', error);
      throw error;
    }
  }

  async updateAsset(id: string, data: Partial<CreateAssetRequest>): Promise<Asset | null> {
    try {
      const asset = await apiService.updateAsset(id, data as any);
      return asset;
    } catch (error) {
      console.error(`❌ Failed to update asset ${id}:`, error);
      return null;
    }
  }

  async deleteAsset(id: string): Promise<boolean> {
    try {
      await apiService.deleteAsset(id);

      // Clear selection if this asset was selected
      if (this.selectedAssetId === id) {
        this.clearSelection();
      }

      return true;
    } catch (error) {
      console.error(`❌ Failed to delete asset ${id}:`, error);
      return false;
    }
  }

  // Asset Selection - Simple localStorage management
  selectAsset(id: string): void {
    this.selectedAssetId = id;
    this.saveSelectedAssetToStorage();
  }

  getSelectedAssetId(): string | null {
    return this.selectedAssetId;
  }

  async getSelectedAsset(): Promise<Asset | null> {
    if (!this.selectedAssetId) return null;
    return this.getAsset(this.selectedAssetId);
  }

  clearSelection(): void {
    this.selectedAssetId = null;
    this.saveSelectedAssetToStorage();
  }

  // File operations live in FileService (upload/delete/set-default with progress tracking).
  // AssetService owns asset CRUD + selection only.

  // Private helper methods
  private saveSelectedAssetToStorage(): void {
    try {
      if (this.selectedAssetId) {
        localStorage.setItem('mabat3d_selected_asset', this.selectedAssetId);
      } else {
        localStorage.removeItem('mabat3d_selected_asset');
      }
    } catch (error) {
      console.warn('Failed to save selected asset to localStorage:', error);
    }
  }

  private loadSelectedAssetFromStorage(): void {
    try {
      const saved = localStorage.getItem('mabat3d_selected_asset');
      if (saved) {
        this.selectedAssetId = saved;
      }
    } catch (error) {
      console.warn('Failed to load selected asset from localStorage:', error);
    }
  }
}

// Singleton instance
export const assetService = new AssetService();
